import { useEffect, useRef, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { cn } from '@buildmate/ui-web';
import { useSession } from '../lib/session-context.tsx';

/**
 * Switch which company the session is acting in.
 *
 * Renders as plain text when there is only one, because a menu that never has
 * a second option is noise. A sub working for two contractors is a normal
 * case rather than an edge one, which is why this exists at all.
 */
export function CompanySwitcher() {
  const { membership, memberships, company, switchCompany } = useSession();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const options = memberships.filter((m) => m.status === 'active');
  const label = company?.name ?? membership.companyName;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (options.length <= 1) {
    return <span className="hidden text-sm text-slate-300 sm:inline">{label}</span>;
  }

  async function choose(companyId: string) {
    if (companyId === membership.companyId) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await switchCompany(companyId);
      setOpen(false);
      // Job ids belong to the company that was active, so stay on a route
      // that is meaningful in any of them.
      await router.navigate({ to: '/app' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-md px-2 py-1 text-sm text-slate-300 hover:bg-slate-800 hover:text-white"
      >
        <span className="max-w-40 truncate">{label}</span>
        <svg
          viewBox="0 0 20 20"
          className="h-4 w-4 shrink-0"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M5.5 7.5L10 12l4.5-4.5z" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Switch company"
          className="absolute right-0 z-50 mt-1 w-64 overflow-hidden rounded-lg bg-white py-1 shadow-lg ring-1 ring-slate-200"
        >
          {options.map((option) => {
            const current = option.companyId === membership.companyId;
            return (
              <button
                key={option.companyId}
                role="menuitem"
                type="button"
                onClick={() => void choose(option.companyId)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50',
                  current ? 'font-medium text-slate-900' : 'text-slate-700',
                )}
              >
                <span className="truncate">{option.companyName}</span>
                {current && (
                  <svg
                    viewBox="0 0 20 20"
                    className="h-4 w-4 shrink-0 text-blue-600"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M8 13.2L4.8 10l-1.3 1.3L8 15.8l8.5-8.5-1.3-1.3z" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
